import { LoginPage } from "@app/pages/login/login-page";
import { OtcPage } from "@app/pages/otc/otc-page";
import { SsoCallbackPage } from "@app/pages/sso/sso-callback-page";
import { Navigate, Route, Routes } from "react-router";
import { ACTIONS } from "@/auth";
import { AuthGuard } from "./components/auth-guard/auth-guard";
import { AccessGuard } from "./components/auth-guard/access-guard";
import { ProtectedRoute } from "./components/auth-guard/protected-route";
import { ErrorBoundary } from "./components/base/error-boundary/error-boundary";
import { LoadingState, LOADING_STATE_VARIANT } from "./components/base/loading-state/loading-state";
import { AppLayout } from "./components/app-layout/app-layout";
import { AccountingApprovalPage } from "./components/accounting-approval/accounting-approval-page";
import { ROUTE_PATH } from "./constants/navigation-routes";
import { useAccessStatus, useCan } from "./hooks/use-can";
import { DashboardPage } from "./pages/dashboard";
import { ErrorPage } from "./pages/error";
import { ForbiddenPage } from "./pages/forbidden";
import { CategoriesPage } from "./pages/categories";
import { GroupsPage } from "./pages/groups";
import { UsersPage } from "./pages/users";
import { CurrencyPolicyPage, ExceptionsPolicyPage, HotelPolicyPage, MileagePolicyPage, ReceiptPolicyPage } from "./pages/policy";
import { PermissionsPage } from "./pages/permissions";
import { NotFoundPage } from "./pages/not-found/not-found-page";

function HomeRoute() {
    const routes = [
        { action: ACTIONS.manage.approvals.read, path: ROUTE_PATH.approval },
        { action: ACTIONS.dashboard.read, path: ROUTE_PATH.home },
        { action: ACTIONS.users.read, path: ROUTE_PATH.users },
        { action: ACTIONS.groups.read, path: ROUTE_PATH.groups },
        { action: ACTIONS.permissions.read, path: ROUTE_PATH.permissions },
        { action: ACTIONS.policy.exceptions.read, path: ROUTE_PATH.policyExceptions },
        { action: ACTIONS.policy.mileage.read, path: ROUTE_PATH.policyMileage },
        { action: ACTIONS.policy.currency.read, path: ROUTE_PATH.policyCurrency },
        { action: ACTIONS.policy.hotel.read, path: ROUTE_PATH.policyHotel },
        { action: ACTIONS.policy.receipt.read, path: ROUTE_PATH.policyReceipt },
        { action: ACTIONS.manage.categories.read, path: ROUTE_PATH.categories },
    ] as const;

    const status = useAccessStatus();
    const canDashboard = useCan(ACTIONS.dashboard.read);
    const canUsers = useCan(ACTIONS.users.read);
    const canGroups = useCan(ACTIONS.groups.read);
    const canPermissions = useCan(ACTIONS.permissions.read);
    const canPolicyExceptions = useCan(ACTIONS.policy.exceptions.read);
    const canPolicyMileage = useCan(ACTIONS.policy.mileage.read);
    const canPolicyCurrency = useCan(ACTIONS.policy.currency.read);
    const canPolicyHotel = useCan(ACTIONS.policy.hotel.read);
    const canPolicyReceipt = useCan(ACTIONS.policy.receipt.read);
    const canApprovals = useCan(ACTIONS.manage.approvals.read);
    const canCategories = useCan(ACTIONS.manage.categories.read);

    const permissions = new Map<string, boolean>([
        [ACTIONS.dashboard.read, canDashboard],
        [ACTIONS.users.read, canUsers],
        [ACTIONS.groups.read, canGroups],
        [ACTIONS.permissions.read, canPermissions],
        [ACTIONS.policy.exceptions.read, canPolicyExceptions],
        [ACTIONS.policy.mileage.read, canPolicyMileage],
        [ACTIONS.policy.currency.read, canPolicyCurrency],
        [ACTIONS.policy.hotel.read, canPolicyHotel],
        [ACTIONS.policy.receipt.read, canPolicyReceipt],
        [ACTIONS.manage.approvals.read, canApprovals],
        [ACTIONS.manage.categories.read, canCategories],
    ]);

    if (status === "loading") {
        return <LoadingState variant={LOADING_STATE_VARIANT.Block} label="Loading permissions" />;
    }

    if (canApprovals) {
        return <Navigate to={ROUTE_PATH.approval} replace />;
    }

    if (canDashboard) {
        return <DashboardPage />;
    }

    const firstAllowedRoute = routes.find((route) => permissions.get(route.action))?.path;

    if (firstAllowedRoute) {
        return <Navigate to={firstAllowedRoute} replace />;
    }

    return <ForbiddenPage />;
}

export function App() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/otc" element={<OtcPage />} />
            <Route path="/sso/callback" element={<SsoCallbackPage />} />
            <Route path={ROUTE_PATH.notFound} element={<NotFoundPage />} />
            <Route path={ROUTE_PATH.error} element={<ErrorPage />} />

            <Route
                element={
                    <AuthGuard>
                        <ErrorBoundary>
                            <AccessGuard>
                                <AppLayout />
                            </AccessGuard>
                        </ErrorBoundary>
                    </AuthGuard>
                }
            >
                <Route path="/" element={<HomeRoute />} />
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute action={ACTIONS.dashboard.read}>
                            <DashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route path={ROUTE_PATH.forbidden} element={<ForbiddenPage />} />
                <Route
                    path="/approval"
                    element={
                        <ProtectedRoute action={ACTIONS.manage.approvals.read}>
                            <AccountingApprovalPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <ProtectedRoute action={ACTIONS.users.read}>
                            <UsersPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/groups"
                    element={
                        <ProtectedRoute action={ACTIONS.groups.read}>
                            <GroupsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/categories"
                    element={
                        <ProtectedRoute action={ACTIONS.manage.categories.read}>
                            <CategoriesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.permissions}
                    element={
                        <ProtectedRoute action={ACTIONS.permissions.read}>
                            <PermissionsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.policyExceptions}
                    element={
                        <ProtectedRoute action={ACTIONS.policy.exceptions.read}>
                            <ExceptionsPolicyPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.policyMileage}
                    element={
                        <ProtectedRoute action={ACTIONS.policy.mileage.read}>
                            <MileagePolicyPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.policyCurrency}
                    element={
                        <ProtectedRoute action={ACTIONS.policy.currency.read}>
                            <CurrencyPolicyPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.policyHotel}
                    element={
                        <ProtectedRoute action={ACTIONS.policy.hotel.read}>
                            <HotelPolicyPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={ROUTE_PATH.policyReceipt}
                    element={
                        <ProtectedRoute action={ACTIONS.policy.receipt.read}>
                            <ReceiptPolicyPage />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route path="*" element={<Navigate to={ROUTE_PATH.notFound} replace />} />
        </Routes>
    );
}
